import { unref as _unref, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "room-image-banner"
}
const _hoisted_2 = ["src", "alt"]
const _hoisted_3 = {
  key: 0,
  class: "image-navigation"
}
const _hoisted_4 = ["disabled"]
const _hoisted_5 = ["disabled"]
const _hoisted_6 = { class: "image-counter" }
const _hoisted_7 = ["href"]
const _hoisted_8 = { class: "room-info-content p-3" }
const _hoisted_9 = {
  key: 0,
  class: "mb-3"
}
const _hoisted_10 = {
  key: 1,
  class: "mb-3"
}
const _hoisted_11 = { class: "fw-bold" }
const _hoisted_12 = {
  key: 2,
  class: "mb-3"
}
const _hoisted_13 = {
  key: 3,
  class: "mb-3"
}
const _hoisted_14 = { class: "small" }
const _hoisted_15 = {
  key: 4,
  class: "mb-3"
}
const _hoisted_16 = {
  key: 5,
  class: "mb-3"
}
const _hoisted_17 = { class: "equipment-list" }
const _hoisted_18 = {
  key: 6,
  class: "mb-3"
}
const _hoisted_19 = { class: "equipment-list" }
const _hoisted_20 = {
  key: 7,
  class: "mb-3"
}
const _hoisted_21 = { class: "d-flex gap-3" }
const _hoisted_22 = { key: 0 }
const _hoisted_23 = { key: 1 }

import { ref, computed, watch } from "vue";
import { useFloating, autoUpdate, offset, flip, shift, size } from "@floating-ui/vue";
import { useMasterDataHelpers } from "../composables/useMasterDataHelpers.js";

// Props
import { onUnmounted } from "vue";


export default {
  __name: 'RoomInfoPopover',
  props: {
	isOpen: {
		type: Boolean,
		default: false,
	},
	roomData: {
		type: Object,
		default: null,
	},
	triggerElement: {
		type: Object,
		default: null,
	},
},
  emits: ["close", "room-calendar-click"],
  setup(__props, { emit: __emit }) {

const props = __props;

// Emits
const emit = __emit;

// Refs
const popoverEl = ref(null);
const currentImageIndex = ref(0);

// Master data helpers
const masterDataHelpers = useMasterDataHelpers();

// Setup floating UI
const { floatingStyles: popoverStyles } = useFloating(
	computed(() => props.triggerElement),
	popoverEl,
	{
		middleware: [
			offset(10),
			flip(),
			shift({ padding: 5 }),
			// On small screens the popover fits on neither side, so cap its height and let it scroll
			size({
				padding: 5,
				apply({ availableHeight, elements }) {
					elements.floating.style.maxHeight = `${Math.max(0, availableHeight)}px`;
				},
			}),
		],
		whileElementsMounted: autoUpdate,
	}
);

// Get complete room data from master data
const completeRoomData = computed(() => {
	if (!props.roomData?.id) return null;
	return masterDataHelpers.getRoomById.value(parseInt(props.roomData.id));
});

// Computed properties using master data
const equipments = computed(() => {
	if (!completeRoomData.value) return [];
	return masterDataHelpers.getRoomEquipments.value(completeRoomData.value);
});

const roomTypes = computed(() => {
	if (!completeRoomData.value) return [];
	return masterDataHelpers.getRoomTypes.value(completeRoomData.value);
});

const roomSize = computed(() => {
	// Check if room has size information
	return completeRoomData.value?.size || completeRoomData.value?.area || null;
});

const buildingInfo = computed(() => {
	return completeRoomData.value?.building || null;
});

// Abbreviation shown next to each booking group; anything unknown counts as view only
const PERMISSION_ABBREVIATIONS = {
	"walk-in-only": "(S)",
	regular: "(B)",
	"without-transaction": "(oP)",
};

const bookingGroups = computed(() =>
	(completeRoomData.value?.bookingGroups || []).map((bookingGroup) => ({
		name: bookingGroup.name,
		abbreviation: PERMISSION_ABBREVIATIONS[bookingGroup.permission] ?? "(A)",
	})),
);

const roomImages = computed(() => {
	// Get all small/thumbnail image URLs from the room's images array
	return (completeRoomData.value?.images || []).map((image) => image.thumbnailUrl);
});

// Methods
const closePopover = () => {
	emit("close");
};

const nextImage = () => {
	if (currentImageIndex.value < roomImages.value.length - 1) {
		currentImageIndex.value++;
	}
};

const previousImage = () => {
	if (currentImageIndex.value > 0) {
		currentImageIndex.value--;
	}
};

const handleRoomCalendarLink = (event) => {
	emit("room-calendar-click", {
		roomData: props.roomData,
		event,
	});
	// Don't automatically close, let parent decide
};

// Watch for clicks and scrolls outside to close popover
watch(
	() => props.isOpen,
	(newValue) => {
		if (newValue) {
			// Reset image index when popover opens
			currentImageIndex.value = 0;
			// Add listeners when popover opens
			setTimeout(() => {
				document.addEventListener("click", handleClickOutside);
				// Capture phase catches scrolls of any container, not only the page
				document.addEventListener("scroll", handleScrollOutside, true);
			}, 100);
		} else {
			// Remove listeners when popover closes
			document.removeEventListener("click", handleClickOutside);
			document.removeEventListener("scroll", handleScrollOutside, true);
		}
	}
);

const handleClickOutside = (event) => {
	if (
		props.isOpen &&
		popoverEl.value &&
		!popoverEl.value.contains(event.target) &&
		props.triggerElement &&
		!props.triggerElement.contains(event.target)
	) {
		closePopover();
	}
};

// The popover follows its trigger, so it would be dragged out of view when the page scrolls
const handleScrollOutside = (event) => {
	// Scrolling the popover's own content must not close it
	if (props.isOpen && popoverEl.value && !popoverEl.value.contains(event.target)) {
		closePopover();
	}
};

// Cleanup on unmount
onUnmounted(() => {
	document.removeEventListener("click", handleClickOutside);
	document.removeEventListener("scroll", handleScrollOutside, true);
});

return (_ctx, _cache) => {
  return (__props.isOpen && __props.roomData)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        ref_key: "popoverEl",
        ref: popoverEl,
        style: _normalizeStyle([_unref(popoverStyles), {"z-index":"1050","max-width":"280px","min-width":"240px","padding":"0"}]),
        class: "room-info-popover popover-content bg-white border shadow-sm rounded"
      }, [
        _createCommentVNode(" Room Image Banner "),
        (roomImages.value.length > 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
              _createElementVNode("img", {
                src: roomImages.value[currentImageIndex.value],
                alt: `Bild von Raum ${completeRoomData.value?.doorLabel || __props.roomData.title}`
              }, null, 8 /* PROPS */, _hoisted_2),
              _createCommentVNode(" Navigation buttons for multiple images "),
              (roomImages.value.length > 1)
                ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                    _createElementVNode("button", {
                      class: "nav-btn prev-btn",
                      disabled: currentImageIndex.value === 0,
                      onClick: previousImage
                    }, [...(_cache[0] || (_cache[0] = [
                      _createElementVNode("i", { class: "fas fa-chevron-left" }, null, -1 /* CACHED */)
                    ]))], 8 /* PROPS */, _hoisted_4),
                    _createElementVNode("button", {
                      class: "nav-btn next-btn",
                      disabled: currentImageIndex.value === roomImages.value.length - 1,
                      onClick: nextImage
                    }, [...(_cache[1] || (_cache[1] = [
                      _createElementVNode("i", { class: "fas fa-chevron-right" }, null, -1 /* CACHED */)
                    ]))], 8 /* PROPS */, _hoisted_5),
                    _createCommentVNode(" Image counter "),
                    _createElementVNode("div", _hoisted_6, _toDisplayString(currentImageIndex.value + 1) + " / " + _toDisplayString(roomImages.value.length), 1 /* TEXT */)
                  ]))
                : _createCommentVNode("v-if", true)
            ]))
          : _createCommentVNode("v-if", true),
        _createElementVNode("a", {
          href: __props.roomData.extendedProps?.url || '#',
          class: "btn btn-primary btn-sm room-link",
          onClick: handleRoomCalendarLink
        }, [...(_cache[2] || (_cache[2] = [
          _createElementVNode("i", { class: "fas fa-calendar-alt me-2" }, null, -1 /* CACHED */),
          _createTextVNode(" Raumkalender öffnen ", -1 /* CACHED */)
        ]))], 8 /* PROPS */, _hoisted_7),
        _createElementVNode("div", _hoisted_8, [
          _createCommentVNode(" Raumname "),
          (
          completeRoomData.value?.name &&
            completeRoomData.value.name !== completeRoomData.value?.doorLabel
        )
            ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                _cache[3] || (_cache[3] = _createElementVNode("div", { class: "small text-muted mb-1" }, " Raumname ", -1 /* CACHED */)),
                _createElementVNode("div", null, _toDisplayString(completeRoomData.value.name), 1 /* TEXT */)
              ]))
            : _createCommentVNode("v-if", true),
          _createCommentVNode(" Raumnummer (doorLabel) "),
          (completeRoomData.value?.doorLabel)
            ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                _cache[4] || (_cache[4] = _createElementVNode("div", { class: "small text-muted mb-1" }, " Raumnummer ", -1 /* CACHED */)),
                _createElementVNode("div", _hoisted_11, _toDisplayString(completeRoomData.value.doorLabel), 1 /* TEXT */)
              ]))
            : _createCommentVNode("v-if", true),
          _createCommentVNode(" Gebäude & Stockwerk "),
          (buildingInfo.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                _cache[5] || (_cache[5] = _createElementVNode("div", { class: "small text-muted mb-1" }, " Gebäude ", -1 /* CACHED */)),
                _createElementVNode("div", null, _toDisplayString(buildingInfo.value.name) + " (" + _toDisplayString(buildingInfo.value.short) + ")", 1 /* TEXT */)
              ]))
            : _createCommentVNode("v-if", true),
          _createCommentVNode(" Hinweis "),
          (completeRoomData.value?.description)
            ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                _cache[6] || (_cache[6] = _createElementVNode("div", { class: "small text-muted mb-1" }, " Hinweis ", -1 /* CACHED */)),
                _createElementVNode("div", _hoisted_14, _toDisplayString(completeRoomData.value.description), 1 /* TEXT */)
              ]))
            : _createCommentVNode("v-if", true),
          _createCommentVNode(" Raumtyp "),
          (roomTypes.value?.length)
            ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                _cache[7] || (_cache[7] = _createElementVNode("div", { class: "small text-muted mb-1" }, " Raumtyp ", -1 /* CACHED */)),
                _createElementVNode("div", null, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(roomTypes.value, (type) => {
                    return (_openBlock(), _createElementBlock("span", {
                      key: type.id,
                      class: "badge bg-secondary me-1"
                    }, _toDisplayString(type.name), 1 /* TEXT */))
                  }), 128 /* KEYED_FRAGMENT */))
                ])
              ]))
            : _createCommentVNode("v-if", true),
          _createCommentVNode(" Buchungsgruppen "),
          (bookingGroups.value.length)
            ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                _cache[8] || (_cache[8] = _createElementVNode("div", { class: "small text-muted mb-1" }, " Buchungsgruppen ", -1 /* CACHED */)),
                _createElementVNode("div", _hoisted_17, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(bookingGroups.value, (bookingGroup, index) => {
                    return (_openBlock(), _createElementBlock("span", {
                      key: index,
                      class: "badge bg-light text-dark border me-1 mb-1"
                    }, _toDisplayString(bookingGroup.name) + " " + _toDisplayString(bookingGroup.abbreviation), 1 /* TEXT */))
                  }), 128 /* KEYED_FRAGMENT */))
                ])
              ]))
            : _createCommentVNode("v-if", true),
          _createCommentVNode(" Ausstattung "),
          (equipments.value?.length)
            ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                _cache[9] || (_cache[9] = _createElementVNode("div", { class: "small text-muted mb-1" }, " Ausstattung ", -1 /* CACHED */)),
                _createElementVNode("div", _hoisted_19, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(equipments.value, (equipment) => {
                    return (_openBlock(), _createElementBlock("span", {
                      key: equipment.id,
                      class: "badge bg-light text-dark border me-1 mb-1"
                    }, _toDisplayString(equipment.name), 1 /* TEXT */))
                  }), 128 /* KEYED_FRAGMENT */))
                ])
              ]))
            : _createCommentVNode("v-if", true),
          _createCommentVNode(" Sitzplätze & m² "),
          (completeRoomData.value?.seats || roomSize.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                _cache[12] || (_cache[12] = _createElementVNode("div", { class: "small text-muted mb-1" }, " Kapazität ", -1 /* CACHED */)),
                _createElementVNode("div", _hoisted_21, [
                  (completeRoomData.value?.seats)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_22, [
                        _cache[10] || (_cache[10] = _createElementVNode("i", { class: "fas fa-users me-1 text-muted" }, null, -1 /* CACHED */)),
                        _createTextVNode(" " + _toDisplayString(completeRoomData.value.seats) + " Sitzplätze ", 1 /* TEXT */)
                      ]))
                    : _createCommentVNode("v-if", true),
                  (roomSize.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_23, [
                        _cache[11] || (_cache[11] = _createElementVNode("i", { class: "fas fa-ruler-combined me-1 text-muted" }, null, -1 /* CACHED */)),
                        _createTextVNode(" " + _toDisplayString(roomSize.value) + " m² ", 1 /* TEXT */)
                      ]))
                    : _createCommentVNode("v-if", true)
                ])
              ]))
            : _createCommentVNode("v-if", true)
        ])
      ], 4 /* STYLE */))
    : _createCommentVNode("v-if", true)
}
}

}